<script setup lang="ts">
/**
 * 自定义设置, as Volcengine's is: the theme as three pictures (浅色, 深色,
 * 双色), then each layout choice as a pair of options — a title,
 * a line on what it does and a picture of it (LayoutThumb) — and the
 * accessibility switches — the layout ones only where the layout can
 * change. Everything takes effect at once and is kept per browser
 * (utils/layout.ts); the bar's height and the menu's width start from the
 * platform's defaults (系统配置) until moved, and 恢复默认 goes back to
 * them. Full screen is the browser's.
 *
 * Rendered once, by App.vue, outside the navbar: the navbar's settings
 * button and the floating one shown while the navbar is off both just set
 * settingsOpen.
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  prefs,
  resetPrefs,
  settingsOpen,
  fullscreen,
  setFullscreen,
  theme,
  THEMES,
  MENU_WIDTH_MIN,
  MENU_WIDTH_MAX,
  NAVBAR_HEIGHT_MIN,
  NAVBAR_HEIGHT_MAX,
  chosenNavbarHeight,
  menuWidth,
} from '@/utils/layout'
import { isNarrow, isPhone } from '@/utils/responsive'
import LayoutThumb from './LayoutThumb.vue'

const { t } = useI18n()

// A layout choice: which pref, and its two options in order.
type Choice = { key: 'navbar' | 'menu' | 'pageTitle'; focus: 'navbar' | 'menu' | 'title' }
const CHOICES: Choice[] = [
  { key: 'navbar', focus: 'navbar' },
  { key: 'menu', focus: 'menu' },
  { key: 'pageTitle', focus: 'title' },
]
// A picture of the console with one choice set so, the rest as they are.
const shape = computed(() => ({ navbar: prefs.navbar, menu: prefs.menu, title: prefs.pageTitle }))
function thumb(c: Choice, on: boolean) {
  return { ...shape.value, [c.focus]: on }
}
const SWITCHES = ['colorWeak', 'grayscale'] as const
</script>

<template>
  <a-drawer
    :visible="settingsOpen"
    :width="isPhone ? '100%' : 380"
    :title="t('custom.title')"
    unmount-on-close
    class="custom-drawer"
    :body-style="{ padding: isPhone ? '16px' : '20px 24px' }"
    @cancel="settingsOpen = false"
  >
    <section class="block">
      <div class="block-title">{{ t('custom.theme') }}</div>
      <div class="themes">
        <button
          v-for="m in THEMES"
          :key="m"
          type="button"
          class="theme"
          :class="{ on: theme === m }"
          :aria-pressed="theme === m"
          @click="theme = m"
        >
          <span class="theme-pic">
            <LayoutThumb :tone="m" />
            <span v-if="theme === m" class="theme-check"><icon-check /></span>
          </span>
          <span class="theme-name">{{ t(`custom.theme_${m}`) }}</span>
        </button>
      </div>
    </section>

    <!-- A narrow screen always has the bar, the menu in a drawer and the
         headings: nothing there to choose. -->
    <section v-for="c in isNarrow ? [] : CHOICES" :key="c.key" class="block">
      <div class="block-title">{{ t(`custom.${c.key}`) }}</div>
      <a-radio-group :model-value="prefs[c.key]" direction="vertical" class="options" @change="(v) => (prefs[c.key] = !!v)">
        <a-radio v-for="on in [true, false]" :key="String(on)" :value="on" class="option">
          <template #radio="{ checked }">
            <div class="option-row" :class="{ checked }">
              <span class="option-dot" />
              <span class="option-text">
                <span class="option-name">{{ t(`custom.${c.key}_${on ? 'on' : 'off'}`) }}</span>
                <span class="option-desc">{{ t(`custom.${c.key}_${on ? 'on' : 'off'}Desc`) }}</span>
              </span>
              <span class="option-pic"><LayoutThumb :tone="theme" v-bind="thumb(c, on)" :focus="on ? c.focus : undefined" /></span>
            </div>
          </template>
        </a-radio>
      </a-radio-group>
      <div v-if="c.key === 'navbar'" class="width-row">
        <span class="width-label">{{ t('layout.navbarHeight') }}</span>
        <a-slider
          v-model="chosenNavbarHeight"
          :min="NAVBAR_HEIGHT_MIN"
          :max="NAVBAR_HEIGHT_MAX"
          :step="4"
          :disabled="!prefs.navbar"
          class="width-slider"
        />
        <span class="width-value">{{ chosenNavbarHeight }}px</span>
      </div>
      <div v-if="c.key === 'menu'" class="width-row">
        <span class="width-label">{{ t('layout.menuWidth') }}</span>
        <a-slider v-model="menuWidth" :min="MENU_WIDTH_MIN" :max="MENU_WIDTH_MAX" :step="10" :disabled="!prefs.menu" class="width-slider" />
        <span class="width-value">{{ menuWidth }}px</span>
      </div>
    </section>

    <section class="block">
      <div class="block-title">{{ t('custom.notify') }}</div>
      <div class="switch-row">
        <span class="option-text">
          <span class="option-name">{{ t('custom.sound') }}</span>
          <span class="option-desc">{{ t('custom.soundDesc') }}</span>
        </span>
        <a-switch v-model="prefs.sound" />
      </div>
      <div class="switch-row">
        <span class="option-text">
          <span class="option-name">{{ t('custom.speech') }}</span>
          <span class="option-desc">{{ t('custom.speechDesc') }}</span>
        </span>
        <a-switch v-model="prefs.speech" />
      </div>
    </section>

    <section class="block last">
      <div class="block-title">{{ t('custom.access') }}</div>
      <div class="switch-row">
        <span class="option-text">
          <span class="option-name">{{ t('app.fullscreen') }}</span>
          <span class="option-desc">{{ t('custom.fullscreenDesc') }}</span>
        </span>
        <a-switch :model-value="fullscreen" @change="(v) => setFullscreen(!!v)" />
      </div>
      <div v-for="k in SWITCHES" :key="k" class="switch-row">
        <span class="option-text">
          <span class="option-name">{{ t(`layout.${k}`) }}</span>
          <span class="option-desc">{{ t(`custom.${k}Desc`) }}</span>
        </span>
        <a-switch v-model="prefs[k]" />
      </div>
    </section>
    <!-- In a div: Arco gives a footer's own buttons a left margin. -->
    <template #footer>
      <div class="foot"><a-button long @click="resetPrefs">{{ t('custom.reset') }}</a-button></div>
    </template>
  </a-drawer>
</template>

<style scoped>
.block {
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid var(--color-border-2);
}
.block.last {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}
.block-title {
  margin-bottom: 16px;
  color: var(--color-text-1);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

/* The themes: three pictures across, the chosen framed in the skin's
   colour with a tick at its corner. */
.themes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}
.theme {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: pointer;
}
.theme-pic {
  position: relative;
  width: 100%;
  padding: 4px;
  border: 2px solid var(--color-border-2);
  transition: border-color 0.1s;
}
.theme:hover .theme-pic {
  border-color: var(--color-border-3);
}
.theme.on .theme-pic {
  border-color: rgb(var(--primary-6));
}
.theme-check {
  position: absolute;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  color: #fff;
  font-size: 12px;
  background: rgb(var(--primary-6));
}
.theme-name {
  max-width: 100%;
  overflow: hidden;
  color: var(--color-text-2);
  font-size: 14px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.theme.on .theme-name {
  color: var(--color-text-1);
  font-weight: 500;
}

/* A choice's options: the dot, its name over a line on what it does,
   and its picture at the right. */
.options {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}
.options :deep(.arco-radio) {
  width: 100%;
  margin: 0;
  padding: 0;
}
.option-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
}
.option-dot {
  box-sizing: border-box;
  flex: none;
  align-self: flex-start;
  width: 18px;
  height: 18px;
  margin-top: 2px;
  border: 1px solid var(--color-border-3);
  border-radius: 50%;
  background: var(--color-bg-2);
  transition: border 0.1s;
}
.option-row:hover .option-dot {
  border-color: rgb(var(--primary-6));
}
.option-row.checked .option-dot {
  border: 5px solid rgb(var(--primary-6));
}
.option-text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.option-name {
  color: var(--color-text-1);
  font-size: 14px;
  line-height: 22px;
}
.option-desc {
  color: var(--color-text-3);
  font-size: 12px;
  line-height: 18px;
}
.option-pic {
  flex: none;
  width: 96px;
}
.width-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-left: 30px;
}
/* One width for both rows' names, so their sliders line up. */
.width-label {
  flex: none;
  width: 72px;
  white-space: nowrap;
  color: var(--color-text-2);
  font-size: 13px;
}
.width-slider {
  flex: 1;
  min-width: 0;
}
.width-value {
  flex: none;
  width: 48px;
  color: var(--color-text-3);
  font-size: 13px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.switch-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.switch-row + .switch-row {
  margin-top: 16px;
}
/* The footer's 16px sides, to the body's 24 on a wide screen. */
@media (min-width: 768px) {
  .foot {
    padding: 0 8px;
  }
}
</style>
